import { useMemo, useState } from 'react'
import {
  getSingleOptionProperties,
  productByProperties,
  type ProductVariationSubset,
} from '@/features/shop/services/Product/utils/variations'

export const useVariationsState = <T extends ProductVariationSubset>({
  product,
  variationProducts,
  onSelectVariationProduct,
}: {
  product: T
  variationProducts: T[]
  onSelectVariationProduct?: (product: T) => void
}) => {
  const singleOptionProperties = useMemo(
    () => getSingleOptionProperties(variationProducts),
    [variationProducts],
  )

  const [selectedProperties, setSelectedProperties] = useState<Record<string, string>>(() =>
    Object.fromEntries(product.variationProperties?.map(({ name, value }) => [name, value]) ?? []),
  )

  const [selectedProduct, setSelectedProduct] = useState(product)
  const [partialMatchProduct, setPartialMatchProduct] = useState<T | null>(null)

  const handleSelectProperty = (name: string, value: string, isAvailable: boolean) => {
    // no-op if a user clicks an already selected property
    if (selectedProperties[name] === value) return

    // if a user clicks an option that is marked as unavailable due to
    // the other currently selected properties, select that option and
    // deselect all other properties
    const nextProperties = {
      ...(isAvailable && selectedProperties),
      [name]: value,
      ...singleOptionProperties,
    }
    setSelectedProperties(nextProperties)
    const result = productByProperties(variationProducts, nextProperties)

    if (!result) return

    const { product: variationProduct, isPartial } = result
    if (isPartial) {
      // this product can be used to preview certain attributes of
      // a product that matches a user's partially selected properties
      // without fully changing the page and triggering network requests
      setPartialMatchProduct(variationProduct)
    } else {
      setPartialMatchProduct(null)
      setSelectedProduct(variationProduct)
      onSelectVariationProduct?.(variationProduct)
    }
  }
  return {
    selectedProperties,
    handleSelectProperty,
    selectedProduct,
    setSelectedProduct,
    partialMatchProduct,
  }
}
